import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import MetricCard from 'src/sections/@dashboard/marketing/components/MetricCard';
import PlatformChip from 'src/sections/@dashboard/marketing/components/PlatformChip';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import { CHART_TOOLTIP_STYLE } from 'src/design/tokens';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const COLORS = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  google_ads: '#FBBC04',
  violet: '#7C3AED',
};

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

export default function OverviewTab({ data }) {
  const { t } = useTranslation('marketing');
  // Os useMemo abaixo precisam rodar em toda renderizacao, mesmo com data
  // ainda nulo (ex.: troca de aba) — um early return antes deles muda a
  // contagem de hooks entre renders e o React derruba com "Rendered more
  // hooks than during the previous render".
  const { totals, daily_comparison, top_posts } = data ?? {};

  const dailyReachData = useMemo(() => {
    if (!daily_comparison) return [];
    const igDays = daily_comparison.instagram || [];
    const fbDays = daily_comparison.facebook || [];

    const dateMap = {};
    igDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, instagram: d.reach };
    });
    fbDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, facebook: d.reach };
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [daily_comparison]);

  const dailyEngagementData = useMemo(() => {
    if (!daily_comparison) return [];
    const igDays = daily_comparison.instagram || [];
    const fbDays = daily_comparison.facebook || [];

    const dateMap = {};
    igDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, instagram: d.engagement };
    });
    fbDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, facebook: d.engagement };
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [daily_comparison]);

  const formatDateTick = (value) => {
    if (!value) return '';
    const parts = value.split('-');
    if (parts.length >= 3) return `${parts[2]}/${parts[1]}`;
    return value;
  };

  if (!data) {
    return <MktLoading />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className={d.stats.grid}>
        <MetricCard
          title={t('analytics.overview.totalFollowers')}
          value={formatNumber(totals?.followers)}
          growth={totals?.followers_growth}
          icon="eva:people-outline"
          color="violet"
        />
        <MetricCard
          title={t('analytics.overview.totalReach')}
          value={formatNumber(totals?.total_reach)}
          icon="eva:eye-outline"
          color="blue"
        />
        <MetricCard
          title={t('analytics.overview.totalImpressions')}
          value={formatNumber(totals?.total_impressions)}
          icon="eva:bar-chart-outline"
          color="amber"
        />
        <MetricCard
          title={t('analytics.overview.totalEngagement')}
          value={formatNumber(totals?.total_engagement)}
          icon="eva:heart-outline"
          color="rose"
        />
      </div>

      {/* Charts Row */}
      <div className={d.charts.grid2}>
        {/* Daily Reach Chart */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>{t('analytics.overview.dailyReach')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyReachData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <Tooltip
                {...CHART_TOOLTIP_STYLE}
                formatter={(value, name) => [formatNumber(value), name === 'instagram' ? t('analytics.tabs.instagram') : t('analytics.tabs.facebook')]}
                labelFormatter={formatDateTick}
              />
              <Legend
                formatter={(value) => (value === 'instagram' ? t('analytics.tabs.instagram') : t('analytics.tabs.facebook'))}
              />
              <Line
                type="monotone"
                dataKey="instagram"
                stroke={COLORS.instagram}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="facebook"
                stroke={COLORS.facebook}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Engagement Chart */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>{t('analytics.overview.dailyEngagement')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyEngagementData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <Tooltip
                {...CHART_TOOLTIP_STYLE}
                formatter={(value, name) => [formatNumber(value), name === 'instagram' ? t('analytics.tabs.instagram') : t('analytics.tabs.facebook')]}
                labelFormatter={formatDateTick}
              />
              <Legend
                formatter={(value) => (value === 'instagram' ? t('analytics.tabs.instagram') : t('analytics.tabs.facebook'))}
              />
              <Bar dataKey="instagram" fill={COLORS.instagram} radius={[4, 4, 0, 0]} />
              <Bar dataKey="facebook" fill={COLORS.facebook} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Posts Table */}
      <div className={d.table.container}>
        <h3 className={d.charts.title}>{t('analytics.overview.topPosts')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={d.table.header}>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('analytics.overview.table.post')}
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('analytics.overview.table.platform')}
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('analytics.overview.table.likes')}
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('analytics.overview.table.comments')}
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('analytics.overview.table.reach')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(top_posts || []).map((post, idx) => (
                <tr key={post.external_id || idx} className={d.table.row}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {post.thumbnail ? (
                        <img
                          src={post.thumbnail}
                          alt=""
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-gray-400 text-xs">—</span>
                        </div>
                      )}
                      <p className="text-sm text-gray-700 line-clamp-2 max-w-xs">
                        {post.caption || t('analytics.noCaption')}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <PlatformChip platform={post.platform} />
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                    {formatNumber(post.likes)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                    {formatNumber(post.comments)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                    {formatNumber(post.reach)}
                  </td>
                </tr>
              ))}
              {(!top_posts || top_posts.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                    {t('analytics.noPosts')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
